import { Send, Sparkles } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react'

import { useAlertFollowup } from '../api/control'
import type { Alert, AlertFollowup } from '../api/types'
import { formatDateTime } from '../lib/format'
import { parseAdviceContext, PortfolioAdviceFindingsView } from './PortfolioAdvisor'

// Mismos valores que app/services/alert_followup.py (MAX_FOLLOWUPS) y el
// schema de la pregunta (max_length=1000).
const MAX_FOLLOWUPS = 20
const MAX_QUESTION_CHARS = 1000
const COUNTER_FROM_CHARS = 800
const STICK_TO_BOTTOM_PX = 80
const MAX_TEXTAREA_ROWS = 5

export function AdvisorConversation({
  alert,
  layout,
}: {
  alert: Alert
  layout: 'narrow' | 'wide'
}) {
  const followup = useAlertFollowup()
  const [draft, setDraft] = useState('')
  const threadRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(false)

  const history = alert.followup_history
  const parsed = parseAdviceContext(alert)
  const awaitingAnswer = followup.isPending
  const pendingQuestion = awaitingAnswer ? followup.variables?.question : undefined
  const limitReached = history.length >= MAX_FOLLOWUPS
  const rows = Math.min(MAX_TEXTAREA_ROWS, Math.max(1, draft.split('\n').length))

  useLayoutEffect(() => {
    const thread = threadRef.current
    if (thread && stickToBottom.current) thread.scrollTop = thread.scrollHeight
  }, [history.length, awaitingAnswer])

  function handleScroll() {
    const thread = threadRef.current
    if (!thread) return
    stickToBottom.current =
      thread.scrollHeight - thread.scrollTop - thread.clientHeight < STICK_TO_BOTTOM_PX
  }

  function send(question: string) {
    stickToBottom.current = true
    followup.mutate(
      { alertId: alert.id, question },
      {
        // Sólo limpia la caja si el usuario no empezó a escribir otra cosa mientras esperaba.
        onSuccess: () => setDraft((current) => (current.trim() === question ? '' : current)),
      },
    )
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const question = draft.trim()
    if (!question || awaitingAnswer || limitReached) return
    send(question)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    event.currentTarget.form?.requestSubmit()
  }

  const errorMessage = followup.isError
    ? followup.error instanceof Error
      ? followup.error.message
      : 'No se pudo enviar la pregunta'
    : null

  return (
    <div className={`advisor-conversation advisor-conversation--${layout}`}>
      <div
        className="advisor-thread"
        ref={threadRef}
        onScroll={handleScroll}
        aria-live="polite"
        aria-relevant="additions"
      >
        <header className="advisor-reading">
          {parsed?.presetLabel && <span className="badge">{parsed.presetLabel}</span>}
          <span className="mono muted">Foto de la flota: {formatDateTime(alert.created_at)}</span>
        </header>

        {parsed ? (
          <PortfolioAdviceFindingsView summary={parsed.summary} findings={parsed.findings} />
        ) : (
          // Alertas viejas (antes de los hallazgos estructurados): sólo queda el texto suelto.
          <p className="advisor-legacy-message">{alert.message}</p>
        )}

        {history.map((item) => (
          <Exchange key={item.timestamp} item={item} />
        ))}

        {pendingQuestion !== undefined && (
          <>
            <div className="advisor-msg advisor-msg--user">
              <p>{pendingQuestion}</p>
            </div>
            <div className="advisor-msg advisor-msg--ai advisor-msg--thinking">
              <Sparkles size={14} aria-hidden="true" />
              <span>Pensando… puede tardar hasta un minuto</span>
              <span className="advisor-dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            </div>
          </>
        )}
      </div>

      {errorMessage && (
        <p className="advisor-error" role="alert">
          {errorMessage}.{' '}
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => followup.variables && send(followup.variables.question)}
          >
            Reintentar
          </button>
        </p>
      )}

      {limitReached ? (
        <p className="muted advisor-limit">
          Conversación completa: pedí una lectura nueva desde el menú del asesor.
        </p>
      ) : (
        <form className="advisor-composer" onSubmit={handleSubmit}>
          <textarea
            rows={rows}
            maxLength={MAX_QUESTION_CHARS}
            placeholder="Preguntá sobre esta lectura…"
            aria-label="Tu pregunta"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={awaitingAnswer}
          />
          {draft.length >= COUNTER_FROM_CHARS && (
            <span className="mono muted advisor-counter">
              {draft.length}/{MAX_QUESTION_CHARS}
            </span>
          )}
          <button
            type="submit"
            className="icon-button icon-button--neutral"
            disabled={!draft.trim() || awaitingAnswer}
            title="Enviar pregunta"
            aria-label="Enviar pregunta"
          >
            <Send size={16} aria-hidden="true" />
          </button>
        </form>
      )}
    </div>
  )
}

function Exchange({ item }: { item: AlertFollowup }) {
  return (
    <>
      <div className="advisor-msg advisor-msg--user">
        <p>{item.question}</p>
        <span className="mono muted">{formatDateTime(item.timestamp)}</span>
      </div>
      <div className="advisor-msg advisor-msg--ai">
        <p>{item.response}</p>
        <span className="mono muted">{formatDateTime(item.timestamp)}</span>
      </div>
    </>
  )
}
